import { useEffect, useState } from 'react'
import Icon from '../Icon'
import { formatPrice } from '../../../shared/money'
import { listPlants, listSpecies, photoUrl, type Plant, type Species } from '../../lib/ppc'

/**
 * My plants — the whole collection.
 *
 * The shape of the working site: eyebrow, a Georgia headline, one primary
 * action, then four counts, then the list with the species rail beside it.
 *
 * The counts are the reason this screen opens first. "Asking-price value" is
 * labelled *not sales revenue* on the site and the label is kept word for word:
 * it is the sum of what things are marked at, and reading it as money taken
 * would be the single most expensive misreading in the app.
 */
export default function PlantList({
  onOpen, onAdd, onSpecies, onPalms, reloadKey, canWrite,
}: {
  onOpen: (plant: Plant) => void
  onAdd: () => void
  onSpecies: () => void
  onPalms: () => void
  reloadKey: number
  canWrite: boolean
}) {
  const [plants, setPlants] = useState<Plant[]>([])
  const [species, setSpecies] = useState<Species[]>([])
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    void Promise.all([
      listPlants({ q: query || undefined, availability: filter || undefined }).catch(() => []),
      listSpecies().catch(() => []),
    ]).then(([p, s]) => { setPlants(p); setSpecies(s) })
      .finally(() => setLoading(false))
  }, [query, filter, reloadKey])

  const identified = species.filter((s) => s.id_status === 'confirmed').length
  const available = plants.filter((p) => p.availability === 'available').length
  const priceValue = plants.reduce((sum, p) => sum + (p.asking_price_cents ?? 0), 0)
  const unpriced = plants.filter((p) => p.asking_price_cents === null).length

  return (
    <>
      <div className="pagehead">
        <div>
          <p className="eyebrow">The whole collection</p>
          <h1>Every plant has a story.</h1>
          <p>Give it a name, a number, and a place in your nursery.</p>
        </div>
        {/*
          * The reference lives up here, not only in the rail beside the list.
          * On a phone the rail stacks BELOW 137 plants, so a button there is a
          * button nobody reaches while standing in the nursery holding one.
          */}
        <div className="actions">
          <button type="button" onClick={onPalms}>
            <Icon name="book" size={15} /> Palm reference
          </button>
          {canWrite && (
            <button type="button" className="primary" onClick={onAdd}>
              <Icon name="plus" size={15} /> Add a plant
            </button>
          )}
        </div>
      </div>

      <dl className="stats">
        <div>
          <dt>Plants cataloged</dt>
          <dd>{plants.length}</dd>
          <small>individual pots</small>
        </div>
        <div>
          <dt>Species identified</dt>
          <dd>{identified}</dd>
          <small>of {species.length} profiles</small>
        </div>
        <div>
          <dt>Available to sell</dt>
          <dd>{available}</dd>
          <small>ready for a new home</small>
        </div>
        <div>
          <dt>Asking-price value</dt>
          <dd>{formatPrice(priceValue)}</dd>
          <small>not sales revenue</small>
        </div>
      </dl>

      <div className="split">
        <div>
          <div className="searchrow">
            <span className="field">
              <Icon name="search" size={16} />
              <input
                value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, species or tag…" aria-label="Search plants"
              />
            </span>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter plants">
              <option value="">All plants</option>
              <option value="available">Available</option>
              <option value="reserved">Reserved</option>
              <option value="sold">Sold</option>
              <option value="not_for_sale">Not for sale</option>
            </select>
          </div>

          {/* The one number worth nagging about before a show. */}
          {unpriced > 0 && (
            <p className="muted small" style={{ marginTop: -8, marginBottom: 16 }}>
              {unpriced} {unpriced === 1 ? 'plant has' : 'plants have'} no asking price yet.
            </p>
          )}

          {loading && <p className="muted">Loading…</p>}

          {!loading && plants.length === 0 && (
            <div className="card empty">
              <Icon name="leaf" size={30} />
              <h3>Start with one pot.</h3>
              <p>Take its photo, write its tag, and add what you know. You can confirm the species later.</p>
              {canWrite && (
                <button type="button" className="primary" onClick={onAdd}>
                  Add your first plant <Icon name="plus" size={15} />
                </button>
              )}
            </div>
          )}

          {plants.length > 0 && (
            <div className="card" style={{ padding: '4px 20px' }}>
              <ul className="plant-list">
                {plants.map((plant) => (
                  <li key={plant.id}>
                    <button type="button" className="plant-row" onClick={() => onOpen(plant)}>
                      {plant.cover_upload_id
                        ? <img className="plant-thumb" src={photoUrl(plant.cover_upload_id)} alt="" loading="lazy" />
                        : <span className="plant-thumb"><Icon name="leaf" size={18} /></span>}
                      <span className="plant-main">
                        <b>{plant.nickname || plant.code}</b>
                        <span>
                          {plant.code}
                          {plant.location ? ` · ${plant.location}` : ''}
                          {plant.is_batch ? ` · ${plant.quantity} in batch` : ''}
                        </span>
                      </span>
                      <span className="plant-meta">
                        <b>{formatPrice(plant.asking_price_cents)}</b>
                        {plant.availability !== 'available' && (
                          <span className={`chip ${plant.availability}`}>
                            {plant.availability.replace(/_/g, ' ')}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <aside className="rail">
          <div className="panel-dark">
            {/*
              * The whole panel opens the species list, not just the button.
              * It reads as a card about species, so it was being tapped as one
              * — and the only thing that did anything was "Add a species",
              * which is a different intention from "show me what I have".
              *
              * A real button rather than an onClick on the div: it has to be
              * reachable by keyboard and announced as a control.
              */}
            <button type="button" className="panel-open" onClick={onSpecies}>
              <span className="panel-top">
                <Icon name="book" size={19} />
                <span>{species.length} profile{species.length === 1 ? '' : 's'}</span>
              </span>
              <span className="panel-title">Learn their names.</span>
              <span className="panel-sub">One species profile connects every plant of that kind.</span>
            </button>
            <button type="button" className="lime" onClick={onSpecies}>
              <Icon name="plus" size={15} /> Add a species
            </button>
            <button type="button" onClick={onPalms}>
              <Icon name="book" size={15} /> Palm reference
            </button>
          </div>
        </aside>
      </div>
    </>
  )
}
